import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Switch, Chip, IconButton, Tooltip, Alert, FormControlLabel, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack,
} from '@mui/material';
import { Refresh as RefreshIcon, Delete as DeleteIcon, CheckCircle as ApproveIcon, Add as AddIcon } from '@mui/icons-material';
import PageHeader from '../../components/PageHeader';
import api from '../../services/api';

export default function OperatorCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [signupAllowed, setSignupAllowed] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    short_name: '',
    admin_email: '',
    has_press_review: true,
    has_theatrical_dcp: false,
    is_approved: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get('/api/operator/companies'),
        api.get('/api/operator/signup-gate'),
      ]);
      setCompanies(c.data);
      setSignupAllowed(s.data.allow_signup);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateCompany = async (id, patch) => {
    try {
      const { data } = await api.patch(`/api/operator/companies/${id}`, patch);
      setCompanies((prev) => prev.map((c) => (c.id === id ? data : c)));
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const deleteCompany = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(null);
    try {
      await api.delete(`/api/operator/companies/${deleteTarget.id}`);
      setCompanies((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
      setDeleteConfirm('');
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setDeleting(false);
    }
  };

  const submitCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const { data } = await api.post('/api/operator/companies', {
        name: createForm.name,
        short_name: createForm.short_name || null,
        admin_email: createForm.admin_email || null,
        has_press_review: createForm.has_press_review,
        has_theatrical_dcp: createForm.has_theatrical_dcp,
        is_approved: createForm.is_approved,
      });
      setCompanies((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setCreateOpen(false);
      setCreateForm({
        name: '',
        short_name: '',
        admin_email: '',
        has_press_review: true,
        has_theatrical_dcp: false,
        is_approved: true,
      });
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setCreating(false);
    }
  };

  const toggleSignup = async () => {
    const next = !signupAllowed;
    try {
      await api.put('/api/operator/signup-gate', { allow_signup: next });
      setSignupAllowed(next);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Companies"
        subtitle="Manage tenant production companies and their subscriptions"
        actions={
          <>
            <Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              Create Company
            </Button>
          </>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControlLabel
          control={<Switch checked={signupAllowed} onChange={toggleSignup} />}
          label={
            <Box>
              <Typography variant="body2" fontWeight={600}>Allow new company signups</Typography>
              <Typography variant="caption" color="text.secondary">
                When off, the public Create Account flow is disabled. Existing companies are unaffected.
              </Typography>
            </Box>
          }
        />
      </Paper>

      {companies.some((c) => !c.is_approved) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {companies.filter((c) => !c.is_approved).length} company signup(s) awaiting approval. Toggle the Approved switch on a row, or click the green check, to grant access.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell sx={{ width: 120 }}>Short Name</TableCell>
              <TableCell align="center">Members</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell align="center">Approved</TableCell>
              <TableCell align="center">Watermarks</TableCell>
              <TableCell align="center">Theatrical DCP</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.map((c) => (
              <TableRow key={c.id} hover sx={!c.is_approved ? { bgcolor: 'rgba(212, 160, 23, 0.06)' } : undefined}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{c.name}</Typography>
                  {!c.is_approved && (
                    <Chip label="Pending approval" size="small" color="warning" variant="outlined" sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }} />
                  )}
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    variant="standard"
                    placeholder="—"
                    defaultValue={c.short_name || ''}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v !== (c.short_name || '')) updateCompany(c.id, { short_name: v });
                    }}
                    inputProps={{ maxLength: 50 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip label={c.member_count} size="small" />
                </TableCell>
                <TableCell align="center">
                  <Switch size="small" checked={c.is_active} onChange={(e) => updateCompany(c.id, { is_active: e.target.checked })} />
                </TableCell>
                <TableCell align="center">
                  <Switch size="small" checked={c.is_approved} onChange={(e) => updateCompany(c.id, { is_approved: e.target.checked })} />
                </TableCell>
                <TableCell align="center">
                  <Switch size="small" checked={c.has_press_review} onChange={(e) => updateCompany(c.id, { has_press_review: e.target.checked })} />
                </TableCell>
                <TableCell align="center">
                  <Switch size="small" checked={c.has_theatrical_dcp} onChange={(e) => updateCompany(c.id, { has_theatrical_dcp: e.target.checked })} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {!c.is_approved && (
                    <Tooltip title="Approve">
                      <IconButton size="small" color="success" onClick={() => updateCompany(c.id, { is_approved: true })}>
                        <ApproveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete company">
                    <IconButton size="small" color="error" onClick={() => { setDeleteTarget(c); setDeleteConfirm(''); }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {companies.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No companies yet.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create company</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Company name"
              required
              fullWidth
              autoFocus
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
            />
            <TextField
              label="Short name"
              fullWidth
              value={createForm.short_name}
              onChange={(e) => setCreateForm({ ...createForm, short_name: e.target.value })}
              helperText="Abbreviation shown in dropdowns and ISDCF DCP names. Optional."
              inputProps={{ maxLength: 50 }}
            />
            <TextField
              label="Admin email"
              type="email"
              fullWidth
              value={createForm.admin_email}
              onChange={(e) => setCreateForm({ ...createForm, admin_email: e.target.value })}
              helperText="If this email matches an existing user, they'll be linked as the new company's admin. Otherwise leave blank — the company is created with no members and you can add one later."
            />
            <Box>
              <Typography variant="caption" color="text.secondary">Products</Typography>
              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={<Switch checked={createForm.has_press_review} onChange={(e) => setCreateForm({ ...createForm, has_press_review: e.target.checked })} />}
                  label="Watermarks"
                />
                <FormControlLabel
                  control={<Switch checked={createForm.has_theatrical_dcp} onChange={(e) => setCreateForm({ ...createForm, has_theatrical_dcp: e.target.checked })} />}
                  label="Theatrical DCP"
                />
              </Stack>
            </Box>
            <FormControlLabel
              control={<Switch checked={createForm.is_approved} onChange={(e) => setCreateForm({ ...createForm, is_approved: e.target.checked })} />}
              label="Approved (active immediately)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={creating || !createForm.name.trim()}>
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete {deleteTarget?.name}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This permanently removes the company and all of its memberships. The
            backend refuses if the company still has jobs, venues, KDMs, packages,
            templates, or other tenant data — close the workspace and clear its
            data first if needed. Type the company name to confirm.
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            placeholder={deleteTarget?.name || ''}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting || deleteConfirm !== deleteTarget?.name}
            onClick={deleteCompany}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
